import { ReactNode, useEffect, useState, useContext } from 'react';

import Cookies from 'js-cookie';
import { useRouter } from 'next/router';

import { Portal, PortalLink } from '../../enums/portal.enum';
import { getFirstPathSegment } from '../../utils/pathUtils';

import PortalContext from './context';

interface PortalProviderProps {
  children: ReactNode;
}

export function PortalProvider({ children }: PortalProviderProps) {
  const [selectedPortal, setSelectedPortal] = useState<Portal | undefined>();
  const router = useRouter();

  useEffect(() => {
    const firstSegment = getFirstPathSegment(router.asPath);

    const segmentToPortal = Object.entries(PortalLink).reduce(
      (acc, [portal, path]) => {
        const segment = path.substring(1);
        return { ...acc, [segment]: portal as Portal };
      },
      {} as Record<string, Portal>,
    );

    if (firstSegment && segmentToPortal[firstSegment]) {
      setSelectedPortal(segmentToPortal[firstSegment]);
    } else {
      setSelectedPortal(undefined);
    }
  }, [router.asPath]);

  const setPortal = (portal: Portal) => {
    setSelectedPortal(portal);
  };

  useEffect(() => {
    if (selectedPortal) {
      Cookies.set('selectedPortal', selectedPortal, { expires: 365 });
    }
  }, [selectedPortal]);

  return (
    <PortalContext.Provider
      value={{
        selectedPortal,
        setSelectedPortal: setPortal,
      }}
    >
      {children}
    </PortalContext.Provider>
  );
}

export function usePortal() {
  const context = useContext(PortalContext);
  if (!context) {
    throw new Error('usePortal must be used within a PortalProvider');
  }
  return context;
}

export default PortalProvider;
